import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGlobalSlice } from "app/slice";
import { toast } from "app/components/ui/use-toast";

import { Label } from "app/components/ui/label";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";

interface Props {
  onVerificationChange?: (simVerified: boolean, imeiVerified: boolean) => void;
}

export const SimCardNumber = ({
  onVerificationChange,
}: Props) => {
  const form = useFormContext();
  const navigate = useNavigate();
  const {
    register,
    formState: { errors },
  } = form;

  // Validation states - make them unique per form instance (using 0 as default index for single form)
  const [simVerified, setSimVerified] = useState<{ [key: number]: boolean }>({});
  const [imeiVerified, setImeiVerified] = useState<{ [key: number]: boolean }>({});

  // Get validation mutations
  const { useValidateSimMutation, useValidateDeviceMutation } =
    useGlobalSlice();
  const [validateSimMutation, { isLoading: simVerifying, isSuccess: simSuccess, isError: simError, error: simErrorData }] =
    useValidateSimMutation();
  const [validateDeviceMutation, { isLoading: imeiVerifying, isSuccess: imeiSuccess, isError: imeiError, error: imeiErrorData }] =
    useValidateDeviceMutation();

  // Handle toast notifications for SIM validation
  useEffect(() => {
    if (simSuccess) {
      toast({
        title: "SIM Number Verified",
        description: "SIM number verified successfully!",
        variant: "success",
      });
    }
  }, [simSuccess]);

  useEffect(() => {
    if (simError) {
      toast({
        title: "SIM Verification Failed",
        description: simErrorData as string,
        variant: "error",
      });
    }
  }, [simError, simErrorData]);

  // Handle toast notifications for IMEI validation
  useEffect(() => {
    if (imeiSuccess) {
      toast({
        title: "IMEI Number Verified",
        description: "IMEI number verified successfully!",
        variant: "success",
      });
    }
  }, [imeiSuccess]);

  useEffect(() => {
    if (imeiError) {
      toast({
        title: "IMEI Verification Failed",
        description: imeiErrorData as string,
        variant: "error",
      });
    }
  }, [imeiError, imeiErrorData]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return;
    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    )
      return;
    if (!/\d/.test(e.key)) e.preventDefault();
  };

  const handleSimValidation = async () => {
    const simNumber = form.getValues("simNumber");
    if (!simNumber) return;

    try {
      // Remove spaces from SIM number before sending to API
      const cleanSimNumber = simNumber.replace(/\s/g, '');
      const result = await validateSimMutation({ sim: cleanSimNumber }).unwrap();
      setSimVerified(prev => ({ ...prev, 0: true }));
      form.clearErrors("simNumber");
    } catch (error: any) {
      setSimVerified(prev => ({ ...prev, 0: false }));
      console.error("SIM validation error:", error);
    }
  };

  const handleImeiValidation = async () => {
    const imeiNumber = form.getValues("imeiNumber");
    if (!imeiNumber) return;

    try {
      const result = await validateDeviceMutation({ imei: imeiNumber }).unwrap();
      setImeiVerified(prev => ({ ...prev, 0: true }));
      form.clearErrors("imeiNumber");
    } catch (error: any) {
      setImeiVerified(prev => ({ ...prev, 0: false }));
      console.error("IMEI validation error:", error);
    }
  };

  const handleContinue = () => {
    navigate('/single-network-number-type');
  };

  // Check if SIM number is valid (19-20 digits)
  const isSimNumberValid = () => {
    const simNumber = form.getValues("simNumber");
    if (!simNumber) return false;
    const iccid = simNumber.replace(/\D/g, "");
    return iccid.length >= 19 && iccid.length <= 20;
  };

  // Check if IMEI number is valid (15 digits)
  const isImeiNumberValid = () => {
    const imeiNumber = form.getValues("imeiNumber");
    if (!imeiNumber) return false;
    const imei = imeiNumber.replace(/\D/g, "");
    return imei.length === 15;
  };

  // Get current verification status for this form instance (using 0 as default index)
  const isSimVerified = simVerified[0] || false;
  const isImeiVerified = imeiVerified[0] || false;

  // Notify parent component of verification status changes
  useEffect(() => {
    if (onVerificationChange) {
      onVerificationChange(isSimVerified, isImeiVerified);
    }
  }, [isSimVerified, isImeiVerified, onVerificationChange]);

  return (
    <React.Fragment>
      <div className="bg-white w-full p-4 sm:p-5 lg:p-6 border border-[#E5E7EB] rounded-xl md:rounded-2xl">
        <h4 className="text-xl font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile mb-3 sm:mb-4 lg:mb-5">
          Enter your sim card number
        </h4>
        <div className="w-full aspect-[224/141] rounded-[1.15rem] sm:rounded-[1.25rem] lg:rounded-[1.375rem] overflow-hidden mb-5 sm:mb-6 lg:mb-[1.875rem]">
          <img
            src="/assets/sim-card-image.webp"
            className="object-cover w-full h-full"
            alt="Sim Card Image"
          />
        </div>
        <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
          <div className="">
            <Label
              htmlFor="simNumber"
              className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Enter the SIM number from the back of your card
            </Label>
            <div className="relative">
              <Input
                id="simNumber"
                type="tel"
                inputMode="numeric"
                maxLength={24}
                {...register("simNumber", {
                  required: "SIM number is required!",
                  validate: {
                    validLength: (value) => {
                      if (!value) return true; // Let required validation handle empty values
                      const iccid = value.replace(/\D/g, "");
                      if (iccid.length < 19 || iccid.length > 20) {
                        return "SIM number must be 19 or 20 digits";
                      }
                      return true;
                    },
                  },
                })}
                onChange={(e) => {
                  const numbers = e.target.value.replace(/\D/g, "");
                  const formatted = numbers.replace(/(\d{4})(?=\d)/g, "$1 ");
                  e.target.value = formatted.slice(0, 24);

                  // Clear error if the number is complete
                  const numericValue = numbers;
                  if (numericValue.length >= 19 && numericValue.length <= 20) {
                    form.clearErrors("simNumber");
                  } else {
                    setTimeout(() => form.trigger("simNumber"), 0);
                  }
                  // Reset verification when input changes
                  setSimVerified(prev => ({ ...prev, 0: false }));
                }}
                onKeyDown={handleKeyDown}
                className="bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] pr-20 border-[#D1D5DB] rounded-xl text-black placeholder:text-black/30 text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
                placeholder="ICCID (19-20 digits)"
              />
              <Button
                type="button"
                variant="ghost"
                className={`absolute right-3 md:right-3.5 top-1/2 -translate-y-1/2 bg-transparent hover:bg-transparent text-[#FE4D01] hover:text-[#E44501] text-[0.72rem] sm:text-xs font-semibold leading-4 sm:leading-5 ${isSimVerified ? "disabled:opacity-100" : ""}`}
                onClick={handleSimValidation}
                disabled={simVerifying || isSimVerified}
              >
                {simVerifying ? (
                  <div className="flex items-center gap-0.5">
                    <span>Verifying</span>
                    <Icons.Spinner className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                  </div>
                ) : isSimVerified ? (
                  <img src="/assets/tick-circle.svg" alt="check" className="size-4 sm:size-[1.125rem] lg:size-5" />
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
            {errors.simNumber && (
              <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                <span className="font-bold">*</span>
                {errors.simNumber?.message as string}
              </p>
            )}
          </div>
          <div className="">
            <Label
              htmlFor="imeiNumber"
              className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
            >
              Enter IMEI Number
            </Label>
            <div className="relative">
              <Input
                id="imeiNumber"
                type="tel"
                inputMode="numeric"
                maxLength={15}
                {...register("imeiNumber", {
                  required: "IMEI number is required!",
                  validate: {
                    exactLength: (value) => {
                      if (!value) return true; // Let required validation handle empty values
                      const imei = value.replace(/\D/g, "");
                      if (imei.length !== 15) {
                        return "IMEI number must be exactly 15 digits";
                      }
                      return true;
                    },
                  },
                })}
                onChange={(e) => {
                  const numbers = e.target.value.replace(/\D/g, "");
                  e.target.value = numbers.slice(0, 15);

                  // Clear error if the number is complete
                  if (numbers.length === 15) {
                    form.clearErrors("imeiNumber");
                  } else {
                    setTimeout(() => form.trigger("imeiNumber"), 0);
                  }
                  // Reset verification when input changes
                  setImeiVerified(prev => ({ ...prev, 0: false }));
                }}
                onKeyDown={handleKeyDown}
                className="bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] pr-20 border-[#D1D5DB] rounded-xl text-black placeholder:text-black/30 text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
                placeholder="IMEI (15 digits)"
              />
              <Button
                type="button"
                variant="ghost"
                className={`absolute right-3 md:right-3.5 top-1/2 -translate-y-1/2 bg-transparent hover:bg-transparent text-[#FE4D01] hover:text-[#E44501] text-[0.72rem] sm:text-xs font-semibold leading-4 sm:leading-5 ${isImeiVerified ? "disabled:opacity-100" : ""}`}
                onClick={handleImeiValidation}
                disabled={imeiVerifying || isImeiVerified}
              >
                {imeiVerifying ? (
                  <div className="flex items-center gap-0.5">
                    <span>Verifying</span>
                    <Icons.Spinner className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                  </div>
                ) : isImeiVerified ? (
                  <img src="/assets/tick-circle.svg" alt="check" className="size-4 sm:size-[1.125rem] lg:size-5" />
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
            {errors.imeiNumber && (
              <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                <span className="font-bold">*</span>
                {errors.imeiNumber?.message as string}
              </p>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
